/**
 * @file DepartmentDropdown.js
 * @description Dropdown component for selecting a department.
 */

import React, { useState, useEffect } from 'react';
import { fetchDepartments } from '../../../services/orgLevelService';
import { Dropdown } from '../../helpers/Dropdown';
import LoadingWrapper from '../../ui/loaders/LoadingWrapper';
import Toast from '../../ui/toasts/Toast';
import { getText } from '../../../i18n';

const DepartmentDropdown = ({
    className,
    inlineLabel = true,
    tenantId,
    selectedDepartment,
    onDepartmentChange,
    isPublic = false,
    companyCode = null,
    required = false,
    errorMessage = null,
}) => {
    const [departments, setDepartments] = useState([]);
    const [defaultValue, setDefaultValue] = useState('Select Department');
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const getDepartments = async () => {
            try {
                const data = await fetchDepartments(tenantId, {
                    isPublic,
                    companyCode,
                });
                setDepartments(data.filter(dept => dept.isActive));

                if (selectedDepartment) {
                    const selectedDept = data.find(
                        dept => dept.id === selectedDepartment,
                    );
                    setDefaultValue(
                        selectedDept ? selectedDept.name : 'Select Department',
                    );
                }
            } catch (error) {
                console.error('Could not fetch departments:', error);
                Toast.error(getText('msg_951289f71b89'));
            } finally {
                setIsLoading(false);
            }
        };

        getDepartments();
    }, [tenantId, selectedDepartment, isPublic, companyCode]);

    const departmentOptions = [
        { value: null, label: getText('msg_b72a3ee1c101') },
        ...departments.map(format => ({
            value: format.id,
            label: format.name,
        })),
    ];

    useEffect(() => {
        if (departments.length > 0 && selectedDepartment) {
            const selectedDept = departments.find(
                dept => dept.id === selectedDepartment,
            );
            setDefaultValue(
                selectedDept ? selectedDept.name : 'Select Department',
            );
        }
    }, [departments, selectedDepartment]);

    const handleChange = value => {
        const selectedDept = departments.find(
            dept => String(dept.id) === value,
        );
        setDefaultValue(selectedDept ? selectedDept.name : 'Select Department');
        onDepartmentChange(value);
    };

    return (
        <div
            className={`form-group mb-3 ${
                inlineLabel ? 'inline-input' : ''
            } ${className}`}
        >
            <label htmlFor="department">
                {getText('msg_db40106a4051')}
                {required && <span className="text-danger">*</span>}
            </label>
            <LoadingWrapper isLoading={isLoading}>
                <Dropdown
                    defaultValue={defaultValue}
                    options={departmentOptions}
                    value={selectedDepartment}
                    onChange={handleChange}
                    disabled={departments.length === 0 || isLoading}
                />
            </LoadingWrapper>
            {errorMessage && (
                <div className="invalid-feedback d-block">{errorMessage}</div>
            )}
        </div>
    );
};

export default DepartmentDropdown;
